import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash';
import uuid from 'uuid/v4';
import nds from '@nerdwallet/base-styles/classes';

import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import Accordion from '@nerdwallet/react-accordion';
import { Text, Heading } from '@nerdwallet/react-typography';

import Row from '../row/row';
import VerticalSpacer from '../vertical-spacer/vertical-spacer';

import './editable-panel-group.less';

import {
  stringTextAreaSpec,
  boolCheckboxSpec,
  repeatable,
} from '../../lib/editing-configs';
import parseMdlinks from '../../lib/parse-md-links';

const textAlignCenter = nds('text-align-center');

const EditablePanelGroup = ({
  title,
  subtitle,
  dangerouslyAllowHTMLInBody,
  panels,
  fullWidthMobile,
  size,
}) => {
  const items = panels.map(
    ({ header = 'Title goes here', body = 'Body copy goes here', key }) => ({
      children: dangerouslyAllowHTMLInBody ? (
        <Text>
          {/* eslint-disable-next-line react/no-danger -- directive added automatically by Shepherd migration */}
          <span dangerouslySetInnerHTML={{ __html: body }} />
        </Text>
      ) : (
        <Text>{parseMdlinks(body)}</Text>
      ),
      title: header,
      key,
    })
  );

  const analytics = {
    enabled: true,
    payload: {
      elementName: 'landing_page_accordion_item',
    },
  };

  let mobileWidth;
  if (fullWidthMobile) {
    mobileWidth = '100%';
  } else {
    mobileWidth = '280px';
  }

  let fullWidthPaddingHorizontal;
  if (fullWidthMobile) {
    fullWidthPaddingHorizontal = '1rem';
  } else {
    fullWidthPaddingHorizontal = '';
  }

  const nodeId = uuid();
  // useState is used to ensure that the trkId is unique per instance
  // and will only be set/updated when the component initially renders
  const [trkId] = useState(nodeId);

  const elementImpressionProps = {
    elementContent: title,
    id: `editable-panel-${trkId}`,
    elementDomType: 'div',
    elementName: 'Accordion',
    sectionCategory: 'EditablePanelGroup',
  };

  return (
    <div
      className="editable-panel__cont"
      style={{
        width: size === 'sm' && `${mobileWidth}`,
        paddingLeft: size === 'sm' && `${fullWidthPaddingHorizontal}`,
        paddingRight: size === 'sm' && `${fullWidthPaddingHorizontal}`,
      }}
    >
      {title && (
        <Heading className={textAlignCenter} level="2">
          {title}
        </Heading>
      )}
      {title && <VerticalSpacer mobile="p-30" tablet="p-30" desktop="p-30" />}
      {subtitle && <Text className="editable-panel__subtitle">{subtitle}</Text>}
      {subtitle && (
        <VerticalSpacer mobile="p-30" tablet="p-30" desktop="p-30" />
      )}
      <Row className="editable-panel">
        <ElementImpression {...elementImpressionProps}>
          <Accordion
            enableTracking
            items={items}
            analytics={analytics}
            id={nodeId}
          />
        </ElementImpression>
      </Row>
      <VerticalSpacer mobile="p-30" tablet="p-30" desktop="p-30" />
    </div>
  );
};

EditablePanelGroup.defaultProps = {
  dangerouslyAllowHTMLInBody: false,
  fullWidthMobile: false,
  panels: [],
  size: '',
};

EditablePanelGroup.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  dangerouslyAllowHTMLInBody: PropTypes.bool,
  fullWidthMobile: PropTypes.bool,
  panels: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      body: PropTypes.string,
    })
  ),
  size: PropTypes.string,
};

EditablePanelGroup.getEditingConfig = () => [
  stringTextAreaSpec('title', 'Title'),
  stringTextAreaSpec('subtitle', 'Subtitle'),
  boolCheckboxSpec('fullWidthMobile', '100% width in mobile viewports?', {
    help:
      'The accordion defaults to a width of 280px on mobile viewports. Check this box to make it 100% width for mobile viewports instead.',
  }),
  boolCheckboxSpec(
    'dangerouslyAllowHTMLInBody',
    'Allow HTML in panel body sections?',
    {
      help:
        'WARNING: We strongly advise against making use of this feature unless you really need it.',
    }
  ),
  repeatable('panels', 'Accordion Sections', 'Section', () => [
    stringTextAreaSpec('header', 'Header'),
    stringTextAreaSpec('body', 'Body'),
  ]),
];

EditablePanelGroup.displayName = 'EditablePanelGroup';

export default connect(
  createStructuredSelector({
    size: _.property('mediaQuery'),
  })
)(EditablePanelGroup);
